import { useToken } from "@contexts/TokenContext";
import { useGetMe } from "@queries/private/useGetMe";
import { parseCurrencyFromURL, type SupportedCurrency } from "@utils/currencyUtils";
import {
  generateAccountCreationMessage,
  generatePhysicalProductMessage,
  generateSubscriptionMessage,
  generateThankYouMessage,
  generateTrainingMessage,
} from "@utils/purchaseMessageUtils";
import {
  detectPurchaseSource,
  determineProductCategory,
  getPurchaseFlags,
  type ProductCategory,
  type PurchaseData,
} from "@utils/purchaseUtils";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

interface PurchaseCompleteState {
  isLoading: boolean;
  purchaseData: PurchaseData | null;
  messages: {
    thankYou: string;
    physicalProduct: string;
    training: string;
    subscription: string;
    accountCreation: string;
  };
  hasUser: boolean;
  category: ProductCategory | null;
  purchaseFlags: {
    hasPhysicalProduct: boolean;
    hasCertifications: boolean;
    hasSubscriptions: boolean;
    hasSubscriptionsOnly: boolean;
    hasCertificationOnly: boolean;
    hasBundlePurchase: boolean;
    hasTrainingOnlyOrBundle: boolean;
  } | null;
  selectedCurrency: SupportedCurrency;
}

export const usePurchaseComplete = (): PurchaseCompleteState => {
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useToken();
  const formMethods = useFormContext();

  // Get user data for authenticated purchases
  const { data: userData, isLoading: isUserLoading, refetch: refetchUser } = useGetMe();

  // Check if user data is available (authenticated users)
  const hasUser = useMemo(() => !!userData, [userData]);

  // Determine purchase source and extract data
  const purchaseData = useMemo((): PurchaseData | null => {
    const hasFormData = formMethods !== null;
    const source = detectPurchaseSource(isAuthenticated ?? false, hasFormData);

    let user: PurchaseData["user"];
    let purchase: PurchaseData["purchase"];

    if (source === "authenticated" && userData && formMethods) {
      // Authenticated purchase with form data
      const formValues = formMethods.getValues();

      user = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        organizationName: userData.organization?.name,
      };

      const items = formValues.items || [];
      const accessories = formValues.accessories || {};

      purchase = {
        uuid: formValues.uuid,
        items,
        accessories,
        categories: [determineProductCategory(items)],
        type: "subscription", // Will be determined by flags
        hasPhysicalProducts: false, // Will be calculated by flags
        hasCertifications: false, // Will be calculated by flags
        hasSubscriptions: false, // Will be calculated by flags
      };
    } else {
      // URL-based purchase (fallback)
      const firstName = searchParams.get("firstName") || "";
      const email = searchParams.get("email") || "";

      user = {
        firstName,
        email,
      };

      // Limited purchase data from URL
      purchase = {
        items: [],
        accessories: {},
        categories: [],
        type: "subscription",
        hasPhysicalProducts: false,
        hasCertifications: false,
        hasSubscriptions: true,
      };
    }

    return {
      source,
      user,
      purchase,
      context: {
        isIframe: hasUser, // iframes are only for authenticated users
      },
    };
  }, [isAuthenticated, userData, formMethods, searchParams, hasUser]);

  // Calculate purchase flags
  const purchaseFlags = useMemo(() => {
    if (!purchaseData || !formMethods) return null;

    const formValues = formMethods.getValues();
    const items = formValues.items || [];
    const selectedAddOns = formValues.accessories || {};

    return getPurchaseFlags(items, selectedAddOns);
  }, [purchaseData, formMethods]);

  // Determine product category
  const category = useMemo(() => {
    if (!purchaseData || !formMethods) return null;

    const formValues = formMethods.getValues();
    const items = formValues.items || [];

    return determineProductCategory(items);
  }, [purchaseData, formMethods]);

  // Extract currency from form context or URL params
  const selectedCurrency = useMemo((): SupportedCurrency => {
    // Try to get from form context first (authenticated users)
    if (formMethods) {
      const formValues = formMethods.getValues();
      const formCurrency = formValues.selectedCurrency;
      if (formCurrency === "usd" || formCurrency === "cad" || formCurrency === "aud") {
        return formCurrency;
      }
    }

    // Fall back to URL params (unauthenticated users)
    return parseCurrencyFromURL(searchParams);
  }, [formMethods, searchParams]);

  // Generate messages based on purchase data
  const messages = useMemo(() => {
    if (!purchaseData) {
      return {
        thankYou: "",
        physicalProduct: "",
        training: "",
        subscription: "",
        accountCreation: "",
      };
    }

    const { user, source } = purchaseData;

    return {
      thankYou: generateThankYouMessage(user.firstName),
      physicalProduct: generatePhysicalProductMessage(),
      training: category ? generateTrainingMessage(category) : "",
      subscription: category ? generateSubscriptionMessage(category) : "",
      accountCreation: generateAccountCreationMessage(user.email, source === "authenticated"),
    };
  }, [purchaseData, category]);

  // Fetch user data when authenticated but not yet loaded
  useMemo(() => {
    if (isAuthenticated && !userData && !isUserLoading) {
      refetchUser();
    }
  }, [isAuthenticated, userData, isUserLoading, refetchUser]);

  return {
    isLoading: !!isAuthenticated && isUserLoading,
    purchaseData,
    messages,
    hasUser,
    category,
    purchaseFlags,
    selectedCurrency,
  };
};
